import dynamic from 'next/dynamic';
import { useCallback, useState } from 'react';
import type { IndustryRatings } from '@/utils/filters/getIndustryRatings';
import Ratings from '@/components/shared/Ratings';
import type { Filter } from '@/models/Filter';

const Checkbox = dynamic(() => import('@cvent/carina/components/Checkbox'), { ssr: false });
const IndustryRatingsAbout = dynamic(() => import('@/components/IndustryRatingsAbout'), { ssr: false });

interface IndustryRatingProps {
  ratings: IndustryRatings;
  selected: Filter;
  setSelected: (selections: Filter) => void;
}

/**
 * Component - Industry rating filter
 */
export function IndustryRatingFilter({ ratings, selected, setSelected }: IndustryRatingProps): JSX.Element {
  const [openRatingsPanel, setOpenRatingsPanel] = useState<boolean>(false);

  const {
    close,
    industryRating,
    industryRatings,
    unratedVenues,
    whatAreIndustryRating,
    aboutIndustryRatings,
    industryRatingsDescription1,
    industryRatingsDescription2,
    northstarRatings,
    northstarRatingsDescription1,
    northstarRatingsDescription2,
    northstarDescriptions,
    northstarDisclaimer
  } = ratings.labels;

  const onDismiss = useCallback(() => {
    setOpenRatingsPanel(false);
  }, [setOpenRatingsPanel]);

  const onRatingsClick = useCallback(
    (value: number) => {
      let selectedRatings = [...(selected.RATINGS?.values || [])];
      if (selectedRatings.includes(value)) {
        selectedRatings = selectedRatings.filter(f => f !== value);
      } else {
        selectedRatings.push(value);
      }
      setSelected({
        ...selected,
        RATINGS: {
          values: selectedRatings,
          includeUnratedVenues: selected.RATINGS?.includeUnratedVenues || false
        }
      });
    },
    [selected, setSelected]
  );

  const onUpdate = useCallback(
    (selection: boolean) => {
      setSelected({
        ...selected,
        RATINGS: { values: selected.RATINGS?.values || [], includeUnratedVenues: selection }
      });
    },
    [selected, setSelected]
  );

  return (
    <>
      <div className="border-y border-neutral-20 py-3" data-cvent-id="af-industry-ratings">
        <div className="mb-4 text-[1.125rem]/relaxed font-medium sm:mb-2 sm:text-b-xl/loose">{industryRating}</div>
        <div className="mb-4 flex justify-between gap-1 py-2 sm:mb-2 sm:gap-2">
          {ratings.star.map(option => {
            const isSelected = selected.RATINGS?.values.includes(option.value);
            return (
              <button
                className={`w-full ${isSelected ? 'bg-brand-10' : ''}`}
                data-cvent-id={`af-industry-ratings-${option.value}`}
                key={`industry-ratings-${option.value}`}
                type="button"
                aria-label={option.label}
                onClick={() => onRatingsClick(Number(option.value))}
              >
                <div
                  className={`box-border flex items-center justify-center rounded-xl border p-3.5 ${
                    !isSelected ? 'border-neutral-40' : 'border-brand-60'
                  }`}
                >
                  <h3 className={`text-b-base sm:text-d-xs ${!isSelected ? 'text-neutral-40' : 'text-brand-60'}`}>
                    {option.value}
                  </h3>
                  <Ratings
                    rating={1}
                    max={1}
                    color={`h-4 w-4 sm:h-6 sm:w-6 ${!isSelected ? 'fill-neutral-40' : 'fill-brand-60'}`}
                  />
                </div>
              </button>
            );
          })}
        </div>
        <div className="mb-4 sm:mb-2">
          <Checkbox
            id="af-industry-rating-include"
            testID="af-industry-rating-include-unrated-venues"
            aria-label={unratedVenues}
            checked={selected.RATINGS?.includeUnratedVenues}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => onUpdate(e.target.checked)}
          >
            <div className="mt-[-0.125rem] text-b-md/snug font-normal">{unratedVenues}</div>
          </Checkbox>
        </div>
        <div className="mb-4 text-b-base/normal sm:mb-2">
          <button
            type="button"
            data-cvent-id="af-industry-rating-what-are-industry-ratings"
            aria-label={whatAreIndustryRating}
            className="text-b-base/normal font-normal text-brand-60"
            onClick={() => setOpenRatingsPanel(true)}
          >
            {whatAreIndustryRating}
          </button>
        </div>
      </div>
      {openRatingsPanel && (
        <IndustryRatingsAbout
          labels={{
            close,
            industryRatings,
            aboutIndustryRatings,
            industryRatingsDescription1,
            industryRatingsDescription2,
            northstarRatings,
            northstarRatingsDescription1,
            northstarRatingsDescription2,
            northstarDescriptions,
            northstarDisclaimer
          }}
          stars={ratings.northStar}
          onDismiss={onDismiss}
          isOpen={openRatingsPanel}
        />
      )}
    </>
  );
}
